import { Component, ElementRef, HostListener, OnInit, inject, signal, viewChild } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../../core/auth/auth.service';
import { LoggingService } from '../../../core/services/logging.service';
import { HlmBtnDirective } from '../../../ui/button.directive';
import { HlmIconComponent } from '../../../ui/icon.component';
import { HlmSwitchComponent } from '../../../ui/switch.component';
import { HlmTooltipDirective } from '../../../ui/misc';
import { ToastService } from '../../../ui/toast.service';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'spike-user-menu',
  standalone: true,
  imports: [AsyncPipe, HlmBtnDirective, HlmIconComponent, HlmSwitchComponent, HlmTooltipDirective],
  template: `
    @if (authService.user$ | async; as user) {
      <div class="relative">
        <button
          type="button"
          (click)="toggle()"
          class="group inline-flex cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-base font-medium text-foreground shadow-sm transition-all hover:border-foreground/30 hover:shadow-md"
          [attr.aria-expanded]="open()"
        >
          <span class="grid h-7 w-7 place-items-center rounded-full bg-primary text-sm font-semibold uppercase tracking-wider text-primary-foreground">
            {{ initials(user.firstname, user.lastname) }}
          </span>
          <span class="hidden sm:inline">{{ user.firstname }} {{ user.lastname }}</span>
          <hlm-icon
            name="lucideChevronDown"
            size="0.85rem"
            [class]="'text-muted-foreground transition-transform ' + (open() ? 'rotate-180' : '')"
          />
        </button>

        @if (open()) {
          <div
            class="absolute right-0 top-full z-50 mt-2 w-96 origin-top-right rounded-xl border border-border bg-popover p-1.5 shadow-[0_2px_4px_rgba(20,18,30,0.04),0_24px_48px_-12px_rgba(20,18,30,0.18)] cmt-reveal"
            style="animation-duration: 0.2s"
          >
            <!-- Identity header -->
            <div class="flex items-start gap-3 p-3">
              <span class="grid h-10 w-10 flex-none place-items-center rounded-full bg-primary text-base font-semibold uppercase tracking-wider text-primary-foreground">
                {{ initials(user.firstname, user.lastname) }}
              </span>
              <div class="min-w-0 flex-1">
                <div class="truncate text-base font-semibold text-foreground">
                  {{ user.firstname }} {{ user.lastname }}
                </div>
                <div class="truncate text-base text-muted-foreground">{{ user.email }}</div>
                <div class="mt-1 font-mono-feat text-base uppercase tracking-[0.18em] text-muted-foreground/70">
                  {{ user.login }}
                </div>
              </div>
            </div>

            @if (authService.ownAccount(); as own) {
              <div class="mx-3 my-1 h-px bg-border"></div>
              <div class="px-3 py-2">
                <div class="cmt-eyebrow text-xs mb-1.5">
                  My account
                </div>
                <div class="flex flex-col gap-1">
                  <div class="group flex items-center gap-2 rounded-md px-2 py-1 text-base text-foreground transition-colors hover:bg-primary/5">
                    <span class="flex-1 text-muted-foreground">Customer</span>
                    <span class="font-mono-feat">{{ own.customerId }}</span>
                    <button
                      type="button"
                      class="grid h-6 w-6 cursor-pointer place-items-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      hlmTooltip="Copy ID"
                      aria-label="Copy customer ID"
                      (click)="copyId(own.customerId); $event.stopPropagation()"
                    >
                      <hlm-icon name="lucideCopy" size="0.85rem" />
                    </button>
                    <button
                      hlmBtn
                      variant="outline"
                      size="sm"
                      aria-label="Open customer"
                      (click)="navigateToCustomer(own.customerId); $event.stopPropagation()"
                    >
                      <hlm-icon name="lucideExternalLink" size="0.9rem" />
                      Open
                    </button>
                  </div>
                  <div class="group flex items-center gap-2 rounded-md px-2 py-1 text-base text-foreground transition-colors hover:bg-primary/5">
                    <span class="flex-1 text-muted-foreground">User Account</span>
                    <span class="font-mono-feat">{{ own.userAccountId }}</span>
                    <button
                      type="button"
                      class="grid h-6 w-6 cursor-pointer place-items-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      hlmTooltip="Copy ID"
                      aria-label="Copy user account ID"
                      (click)="copyId(own.userAccountId); $event.stopPropagation()"
                    >
                      <hlm-icon name="lucideCopy" size="0.85rem" />
                    </button>
                    <button
                      hlmBtn
                      variant="outline"
                      size="sm"
                      aria-label="Open user account"
                      (click)="navigateToUa(own.customerId, own.userAccountId); $event.stopPropagation()"
                    >
                      <hlm-icon name="lucideExternalLink" size="0.9rem" />
                      Open
                    </button>
                  </div>
                  @if (own.phoneServiceId !== null) {
                    <div class="group flex items-center gap-2 rounded-md px-2 py-1 text-base text-foreground transition-colors hover:bg-primary/5">
                      <span class="flex-1 text-muted-foreground">Phone Service</span>
                      <span class="font-mono-feat">{{ own.phoneServiceId }}</span>
                      <button
                        type="button"
                        class="grid h-6 w-6 cursor-pointer place-items-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                        hlmTooltip="Copy ID"
                        aria-label="Copy phone service ID"
                        (click)="copyId(own.phoneServiceId!); $event.stopPropagation()"
                      >
                        <hlm-icon name="lucideCopy" size="0.85rem" />
                      </button>
                      <button
                        hlmBtn
                        variant="outline"
                        size="sm"
                        aria-label="Open phone service"
                        (click)="navigateToPs(own.customerId, own.phoneServiceId!); $event.stopPropagation()"
                      >
                        <hlm-icon name="lucideExternalLink" size="0.9rem" />
                        Open
                      </button>
                    </div>
                  }
                </div>
              </div>
            }

            @if (user.roles?.length) {
              <div class="mx-3 my-1 h-px bg-border"></div>
              <div class="px-3 py-2">
                <div class="cmt-eyebrow text-xs mb-1.5">
                  Roles
                </div>
                <div class="flex flex-wrap gap-1.5">
                  @for (role of user.roles; track role) {
                    <span class="rounded-md bg-muted px-2 py-0.5 font-mono-feat text-base text-foreground">
                      {{ role }}
                    </span>
                  }
                </div>
              </div>
            }

            <div class="mx-3 my-1 h-px bg-border"></div>
            <div class="px-3 py-2">
              <div class="cmt-eyebrow text-xs mb-1.5">
                Diagnostics
              </div>
              <div
                class="flex min-h-11 items-center gap-2 rounded-md px-2 py-1 text-base text-foreground"
                (click)="$event.stopPropagation()"
              >
                <span class="flex-1 text-muted-foreground">Verbose logging</span>
                <hlm-switch
                  data-testid="verbose-logging-toggle"
                  ariaLabel="Verbose logging"
                  [checked]="loggingService.isVerbose()"
                  (checkedChange)="onVerboseToggle($event)"
                />
              </div>
            </div>

            <div class="mx-3 my-1 h-px bg-border"></div>
            <div class="px-3 py-2">
              <button
                hlmBtn
                variant="destructive"
                size="sm"
                type="button"
                (click)="logout()"
                class="w-full"
              >
                <hlm-icon name="lucideLogOut" size="0.9rem" />
                Sign out
              </button>
            </div>
          </div>
        }
      </div>
    }
  `,
})
export class SpikeUserMenuComponent implements OnInit {
  authService = inject(AuthService);
  readonly loggingService = inject(LoggingService);
  private router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private elRef = inject(ElementRef);
  private toast = inject(ToastService);
  readonly open = signal(false);

  /** The verbose-logging switch, reset imperatively when the user cancels the confirm. */
  private readonly verboseSwitch = viewChild(HlmSwitchComponent);

  ngOnInit(): void {
    // Defensive: failure to prefetch own-account must not prevent the menu
    // from rendering the identity header. Errors are swallowed; the "My
    // account" section simply won't appear when ownAccount() stays null.
    try {
      this.authService.ensureOwnAccountLoaded();
    } catch {
      // ignore — degraded mode, identity header still renders
    }
  }

  toggle(): void { this.open.update(v => !v); }
  logout(): void { this.open.set(false); this.authService.logout(); }

  /**
   * D12 verbose-logging opt-in. It is a support/diagnostics aid meant to be used
   * ONLY when the development team asks for it — the confirm-dialog copy makes
   * that explicit and deliberately avoids internal jargon (no "Graylog"). Turning
   * it ON is guarded by the confirm dialog; turning it OFF applies immediately.
   * On cancel the optimistic switch flip is reverted so the toggle keeps
   * reflecting the real logging state. Every deliberate flip emits
   * `verbose_logging.toggle` so we can see when detailed logging was enabled.
   *
   * @ticket EB-3798
   */
  onVerboseToggle(next: boolean): void {
    // `HlmSwitch.checked` is a `model()`, so writeValue() (our reset below) also
    // fires (checkedChange) and RE-ENTERS this handler. Ignore any echo that does
    // not actually differ from the real logging state — only a genuine user flip
    // (next !== the current verbose state) is an action. This kills the phantom
    // `verbose_logging.toggle{enabled:false}` the reset used to emit while still
    // letting us reset the switch. (PR #43 review — caught on the test sweep.)
    if (next === this.loggingService.isVerbose()) {
      return;
    }

    if (!next) {
      this.loggingService.setVerbose(false);
      this.loggingService.usage('verbose_logging.toggle', { enabled: false });
      return;
    }

    // Undo the switch's optimistic local flip BEFORE the dialog opens, so
    // aria-checked never reads ON while the confirm is still asking (a screen
    // reader would otherwise announce "switch on" prematurely). The re-entry from
    // this writeValue is swallowed by the guard above; the real ON state is
    // driven by the [checked] binding only on confirm.
    this.verboseSwitch()?.writeValue(false);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Enable verbose logging?',
        message:
          'Only turn this on if the development team asked you to. It records ' +
          'detailed diagnostic information for this session only — it switches ' +
          'off automatically when you close the tab, or you can turn it off here.',
        confirmLabel: 'Enable',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loggingService.setVerbose(true);
        this.loggingService.usage('verbose_logging.toggle', { enabled: true });
      }
      // On cancel: nothing to do — the switch was already reset above.
    });
  }

  navigateToCustomer(customerId: number): void {
    this.open.set(false);
    this.router.navigate(['/', customerId]);
  }
  navigateToUa(customerId: number, uaId: number): void {
    this.open.set(false);
    this.router.navigate(['/', customerId, 'ua', uaId, 'account-details']);
  }
  navigateToPs(customerId: number, psId: number): void {
    this.open.set(false);
    this.router.navigate(['/', customerId, 'ps', psId, 'settings']);
  }

  copyId(id: number): void {
    const text = String(id);
    const onSuccess = (): void => {
      this.toast.open(`Copied ${text}`, undefined, { tone: 'success', duration: 1500 });
    };
    const onError = (): void => {
      this.toast.open(`Failed to copy ${text}`, undefined, { tone: 'error', duration: 2500 });
    };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess, onError);
    } else {
      onError();
    }
  }

  initials(first?: string, last?: string): string {
    const f = (first || '').trim()[0] || '';
    const l = (last || '').trim()[0] || '';
    return (f + l).toUpperCase() || '?';
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (this.open() && !this.elRef.nativeElement.contains(e.target)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void { this.open.set(false); }
}
